import React, { Suspense, useEffect, useState, useRef, useMemo } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
// Removed Redux imports to make this a reusable presentation component
// import { useAppDispatch, RootState } from "../redux-toolkit/store/store";
// import { handleUpdatePainLocations } from "../redux-toolkit/slices/painEntryFormSlice";
// import { useSelector } from "react-redux";
// import { bodyPainColorsByRating } from "../helpers/painEntryFormConstants";

const AutoRotatingBody: React.FC<any> = ({ scale, isBodyClickEnable, painLocations = [] }) => {
  // Removed Redux hooks
  // const { userPainData } = useSelector(
  //   (state: RootState) => state.dashboard
  // );
  // const dispatch = useAppDispatch();
  
  // const [colors, setColors] = useState<any>(bodyPainColorsByRating);
  const gltf = useLoader(GLTFLoader, "/models/3dbodytwinge.glb");
  const scene = useMemo(() => SkeletonUtils.clone(gltf.scene), [gltf.scene]);
  const [selectedParts, setSelectedParts] = useState(new Set<string>());
  const groupRef = useRef<THREE.Group>(null);

  useEffect(() => {
    // Initialize selected parts from props
    setSelectedParts(new Set(painLocations));
  }, [painLocations]);

  useEffect(() => {
    if (scene) {
      // Loop through the parts and apply the color change based on selectedParts
      scene.children.forEach((child) => {
        const partName = child.userData?.name; // Retrieve part name from userData
        child.traverse((node: any) => {
          if (node.isMesh) {
            // Change color based on selection
            if (selectedParts.has(partName)) {
              // Removed rating logic as we don't have location averages in this context
              // Defaulting to red for selected pain locations
              const color = "red";
              node.material = new THREE.MeshStandardMaterial({
                color: color,
              });
            } else {
              node.material = new THREE.MeshStandardMaterial({
                color: "white", // Default color for unselected parts
              });
            }
          }
        });
      });
    }
  }, [scene, selectedParts]);

  // Handle click event on model parts
  const handleClick = (mesh: any) => {
    if (mesh.isMesh) {
      const partName = mesh.userData?.name; // Retrieve part name

      // Update selected parts when clicked
      setSelectedParts((prevSelected) => {
        const newSelected = new Set(prevSelected);
        if (newSelected.has(partName)) {
          newSelected.delete(partName); // Unselect part if already selected
          mesh.material.color.set("white"); // Revert to default color
        } else {
          newSelected.add(partName); // Select part
          mesh.material.color.set("red"); // Highlight selected part
        }
        return newSelected;
      });
    }
  };

  // Removed dispatching updates back to store
  // useEffect(() => {
  //   const painLocations = Array.from(selectedParts);
  //   dispatch(handleUpdatePainLocations(painLocations));
  // }, [selectedParts, dispatch]);

  // Apply auto-rotation to the model
  useFrame((state, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += 0.5 * delta; // Adjust speed here
    }
  });

  return (
    <group ref={groupRef}>
      <primitive
        object={scene}
        scale={scale}
        position={[0, -3, 0]}
        onPointerDown={(e: any) => {
          if (isBodyClickEnable) {
            handleClick(e.object);
          }
        }} // Handle click event
      />
    </group>
  );
};

interface IMiniBodyViewer {
  isBodyClickEnable?: boolean;
  painLocations?: string[];
}

const MiniBodyViewer: React.FC<IMiniBodyViewer> = ({
  isBodyClickEnable = false,
  painLocations = [],
}) => {
  // Hardcoded scale for the mini viewer
  const scale = 0.25;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "300px", // Limit width for "mini" feel
        margin: "0 auto",
        padding: "0px", // Reduced padding
        borderRadius: "8px",
        // backgroundColor: "#fff", // Optional: transparent or white
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start", // Changed from center to flex-start
      }}
    >
      <div style={{ width: "100%", height: "250px", marginTop: "-60px" }}> {/* Negative margin to pull canvas up */}
        <Canvas>
          <Suspense fallback={null}>
            <ambientLight />
            <pointLight position={[10, 10, 10]} />
            <AutoRotatingBody
              isBodyClickEnable={isBodyClickEnable}
              scale={[scale, scale, scale]}
              painLocations={painLocations}
            />
            <OrbitControls enableZoom={false} enablePan={false} />
          </Suspense>
        </Canvas>
      </div>
    </div>
  );
};

export default React.memo(MiniBodyViewer);
